export interface HookProps {
  onIncludeDeleted: (checked: boolean) => void;
  onEnableAutocomplete: (checked: boolean) => void;
}
const useSearchOptionsDropdownLogic = ({
  onIncludeDeleted,
  onEnableAutocomplete,
}: HookProps) => {
  const handleDeletedCheckbox = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    onIncludeDeleted(checked);
  };

  const handleAutocompleteCheckbox = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    onEnableAutocomplete(checked);
  };

  /**
   * This function will calculate the necessary negative left percentage value that
   * needs to be set on the background border gradient of the query anywhere search dropdown
   * so that the border gradient of the dropdown (which is always a fixed 708px) always matches
   * the gradient of the search anywhere container that spans the entire length of the window.
   *
   * @returns the percentage that will need to be set as a negative left value of the search
   * dropdown border gradient
   */
  const calculateLeftAlignPercentageForGradientBorder = () => {
    // Get dimensions of parent 'query anywhere' container that has gradient background
    const queryAnywhereContainerDimensions = document
      .getElementById('targetForWidthofLinearGradient')
      ?.getBoundingClientRect();

    // Get dimensions of query anywhere search dropdown background
    const dropdownBackgroundDimensions = document
      .getElementById('dropdownBackgroundContainer')
      ?.getBoundingClientRect();

    // Get the width of the query anywhere search dropdown background. If it returns undefined or 0,
    // set the width to 708 (the actual fixed size of the search dropdown, no matter what the screen size is)
    const dropdownBackgroundWidth =
      dropdownBackgroundDimensions && dropdownBackgroundDimensions.width !== 0
        ? dropdownBackgroundDimensions.width
        : 708;
    let queryAnywhereContainerWidth = 0;
    // If the width of the parent query anywhere container exists and is not 0, set the width
    // otherwise, set it to the entire window width since the query anywhere container is the same width as the entire window
    if (
      queryAnywhereContainerDimensions &&
      queryAnywhereContainerDimensions.width !== 0
    ) {
      queryAnywhereContainerWidth = queryAnywhereContainerDimensions.width;
    } else {
      queryAnywhereContainerWidth = window.innerWidth;
    }
    const widthOfQueryAnywhereMinusSearchDecimal =
      (queryAnywhereContainerWidth - dropdownBackgroundWidth) /
      queryAnywhereContainerWidth;
    // This is the percentage of the width of the query anywhere container minus the width of the search dropdown container
    const widthOfQueryAnywhereMinusSearchPercentage =
      widthOfQueryAnywhereMinusSearchDecimal * 100;

    /* 
      Once the window is resized to be less than 1280, the search dropdown becomes centered within the parent container. 
      If the search dropdown is centered, the negative left value can be set to slightly smaller than the width (hence 
      multiplying by .9) of the parent query anywhere container minus the search dropdown width.
      However, if the window size is greater than or equal to 1280, the search dropdown now becomes moved slightly to the 
      right, making it off-center because of the 'Search for Anything" label that now appears. This complicates the calculation 
      so that it needs to be a certain number of times bigger than the width of the query anywhere container. 
    */
    const widthMultiplierBasedOnScreenSize =
      queryAnywhereContainerWidth < 1280
        ? 0.9
        : 1 + widthOfQueryAnywhereMinusSearchDecimal;
    const leftAlignPercentageForDropdownGradient =
      widthOfQueryAnywhereMinusSearchPercentage *
      widthMultiplierBasedOnScreenSize;

    return leftAlignPercentageForDropdownGradient;
  };

  return {
    handleDeletedCheckbox,
    handleAutocompleteCheckbox,
    calculateLeftAlignPercentageForGradientBorder,
  };
};

export default useSearchOptionsDropdownLogic;
